// Pure helpers for the AI-trial upgrade page: money/date formatting, resolving the AI Tracking
// price from the product catalogue, and turning the current subscription + preview response into
// the "Today's Billing Summary" rows shown in the design.

// AI Tracking is added on conversion at a fixed quantity the user cannot change. Mirrors
// FREE_AI_TRACKING_QUANTITY in customers/services/manage_subscription/migrate_legacy_subscription.py.
export const FREE_AI_TRACKING_QUANTITY = 100;

// product_slug of the AI Tracking product in the /registration catalogue (see BILLING_IDS).
export const AI_TRACKING_SLUG = 'ai_tracking';

const CURRENCY_SYMBOLS = { usd: '$', eur: '€', gbp: '£' };

/** Format an integer amount of cents as a currency string, e.g. (29900, "usd") -> "$299.00". */
export function formatMoney(cents, currency = 'usd') {
    const symbol = CURRENCY_SYMBOLS[(currency || 'usd').toLowerCase()] || '$';
    const value = (Math.abs(Number(cents) || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const sign = (Number(cents) || 0) < 0 ? '-' : '';
    return `${sign}${symbol}${value}`;
}

/** Format an ISO date (or Date) as "Jun 18, 2025". Returns '' for empty input. */
export function formatDate(input) {
    if (!input) return '';
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Whole days from now until the given ISO date (never negative). */
export function daysUntil(input) {
    if (!input) return 0;
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return 0;
    const diff = date.getTime() - Date.now();
    return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

/** Add whole calendar months to a date, returning a new Date. */
export function addMonths(date, months) {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
}

/** Add a number of days to a date, returning a new Date. */
export function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Add `count` billing periods of a Stripe interval ('day' | 'week' | 'month' | 'year') to a date.
 * Generalizes addMonths/addDays so period boundaries (e.g. "Current billing period") are correct
 * for non-monthly legacy plans instead of assuming a one-month cycle.
 */
export function addInterval(date, interval, intervalCount, count = 1) {
    const n = (Number(intervalCount) || 1) * count;
    if (interval === 'year') {
        const result = new Date(date);
        result.setFullYear(result.getFullYear() + n);
        return result;
    }
    if (interval === 'week') return addDays(date, n * 7);
    if (interval === 'day') return addDays(date, n);
    return addMonths(date, n);
}

/**
 * "/ month", "/ year", "/ 2 years" ... a price suffix matching this page's existing "/ <unit>"
 * copy style, driven by the subscription's actual Stripe interval instead of a hardcoded month.
 */
export function intervalSuffix(interval, intervalCount) {
    const units = { day: 'day', week: 'week', month: 'month', year: 'year' };
    const unit = units[interval] || 'month';
    const count = Number(intervalCount) || 1;
    return count > 1 ? `/ ${count} ${unit}s` : `/ ${unit}`;
}

// Length of the free AI Tracking trial, in days (matches the "free for 30 days" copy).
export const AI_TRIAL_DAYS = 30;

/**
 * Resolve the per-unit amount (cents) for a catalogue price dict, handling tiered billing with
 * Stripe "volume" semantics. Flat prices expose unit_amount directly; tiered prices carry their
 * amounts in `tiers`. Returns null when neither is available.
 */
function resolvePerUnitCents(price, quantity) {
    if (price?.unit_amount != null) return price.unit_amount;
    for (const tier of price?.tiers || []) {
        const upTo = tier.up_to;
        if (upTo == null || quantity <= upTo) {
            return tier.unit_amount ?? null;
        }
    }
    return null;
}

/** Find a catalogue price billing at exactly the given Stripe interval. */
function findPriceAtInterval(prices, interval, intervalCount) {
    return (prices || []).find(
        (price) =>
            price?.recurring?.interval === interval &&
            (price?.recurring?.interval_count || 1) === (Number(intervalCount) || 1),
    );
}

/**
 * Find the AI Tracking price in the /registration catalogue that bills at the same interval as the
 * user's current subscription, so the product they are adding lines up with the rest of their plan
 * (an annual subscriber gets the annual AI price, not a monthly one).
 *
 * Falls back to the monthly price when the catalogue has nothing at that interval — the same
 * exact-interval-then-monthly chain the backend already uses when it projects a legacy plan onto
 * new-system prices (GetCurrentSubscriptionService._resolve_legacy_price). That fallback also
 * covers the odd intervals `current.interval` can report, e.g. a trialing item whose current price
 * is day-based.
 *
 * Returns null when the catalogue has no usable AI Tracking price at all.
 */
export function resolveAiPrice(activePlans, interval, intervalCount) {
    const plan = (activePlans || []).find((p) => p.product_slug === AI_TRACKING_SLUG);
    if (!plan) return null;

    const price =
        findPriceAtInterval(plan.prices, interval, intervalCount) ||
        findPriceAtInterval(plan.prices, 'month', 1);
    if (!price) return null;

    const recurring = price.recurring || {};
    return {
        price_id: price.id,
        per_unit_cents: resolvePerUnitCents(price, FREE_AI_TRACKING_QUANTITY),
        product_id: plan.product_id,
        product_name: plan.product_name || '',
        interval: recurring.interval || 'month',
        interval_count: recurring.interval_count || 1,
    };
}

/**
 * Build the `price_items` payload describing the subscription the user is about to end up on:
 * every product they already pay for, at its current quantity, plus AI Tracking.
 *
 * The AI Tracking item has to be submitted explicitly. `with_free_ai_tracking` is only a
 * trial-LENGTH switch on the backend (30 days instead of 14) — it does not add the product for you,
 * and the API rejects the request outright when the flag is set without a matching AI Tracking
 * price item. The same list is used for the preview and for the confirm PATCH so the two can never
 * describe different subscriptions.
 *
 * Returns null when the catalogue gave us no AI Tracking price, since there is nothing to preview
 * or commit in that case.
 */
export function buildPriceItems(currentItems, aiPrice) {
    if (!aiPrice?.price_id) return null;

    const items = (currentItems || [])
        .filter((item) => item.price_id && item.product_id !== aiPrice.product_id)
        .map((item) => ({ price_id: item.price_id, quantity: item.quantity }));

    items.push({ price_id: aiPrice.price_id, quantity: FREE_AI_TRACKING_QUANTITY });
    return items;
}

/** Sum the current subscription items' recurring amount (unit_amount * quantity) in cents. */
export function sumCurrentItemsCents(items) {
    return (items || []).reduce(
        (total, item) => total + (Number(item.unit_amount) || 0) * (Number(item.quantity) || 0),
        0,
    );
}

/**
 * Resolve the price and product label(s) to show for the "Current Subscription" box.
 *
 * A legacy (v1/"traditional") user's `items` on GET /subscription/ are projected onto the new
 * system's products/prices so the rest of the app can render them uniformly — but that means their
 * unit_amount/product_name no longer reflect what the user is actually being charged today. When
 * `legacy_plan` is present it carries the real, unprojected v1 numbers (see docs/BILLING.md), so it
 * takes priority; only fall back to the projected items for a subscription with no legacy_plan.
 */
export function resolveCurrentPlanDisplay(current) {
    const legacyPlan = current?.legacy_plan?.plan;
    if (legacyPlan?.amount != null) {
        return {
            priceCents: Number(legacyPlan.amount) || 0,
            productLabels: legacyPlan.name ? [legacyPlan.name] : [],
        };
    }
    return {
        priceCents: sumCurrentItemsCents(current?.items),
        productLabels: (current?.items || []).map((item) => item.product_name).filter(Boolean),
    };
}

/**
 * Normalize the preview invoice's lines into a display-ready shape.
 *
 * Every line carries its own `is_proration` flag, so we no longer have to infer meaning from the
 * sign alone: non-proration lines are the fresh full period being charged ("New subscription
 * starting today"), while proration lines are the billing-reset adjustments — negative ones are the
 * credit for the unused portion of the current period.
 */
export function normalizePreviewLines(preview, aiPrice) {
    const aiName = (aiPrice?.product_name || '').toLowerCase();

    return (preview?.lines || []).map((line) => {
        const description = line.description || '';
        const amountCents = Number(line.amount_cents) || 0;
        const quantity = line.quantity == null ? null : Number(line.quantity);
        // Spot the AI Tracking line so the summary can label it with the "first 30 days" copy
        // instead of Stripe's raw description. Stripe embeds the product name in the description,
        // which is the reliable signal; the quantity/amount check is the fallback for when the
        // catalogue did not give us a name.
        const isAiTracking = aiName
            ? description.toLowerCase().includes(aiName)
            : amountCents === 0 && quantity === FREE_AI_TRACKING_QUANTITY;

        return {
            description,
            amountCents,
            quantity,
            isProration: Boolean(line.is_proration),
            isAiTracking,
        };
    });
}

/**
 * Split normalized lines into the fresh-period charges and the proration adjustments.
 *
 * `recurringCents` is the sum of the non-proration lines — the amount the subscription costs for a
 * full period, which is what the plan cards show as "/ month". `creditCents` is the absolute value
 * of the negative proration lines (unused time coming back), and `prorationChargeCents` covers the
 * rare positive proration line so the rows still reconcile with the subtotal.
 */
export function splitPreviewLines(preview, aiPrice) {
    const lines = normalizePreviewLines(preview, aiPrice);
    let recurringCents = 0;
    let creditCents = 0;
    let prorationChargeCents = 0;

    for (const line of lines) {
        if (!line.isProration) {
            recurringCents += line.amountCents;
        } else if (line.amountCents < 0) {
            creditCents += -line.amountCents;
        } else {
            prorationChargeCents += line.amountCents;
        }
    }

    return { lines, recurringCents, creditCents, prorationChargeCents };
}

/**
 * Build the billing-summary figures for the page from the preview response
 * (PATCH /api/v2/accounts/subscription/?preview=true), which is the authority on what is charged
 * today. The response now reports the invoice totals explicitly instead of leaving us to derive
 * them from the lines:
 *   - subtotal_cents      -> sum of all lines, before discounts
 *   - total_discount_cents-> coupons/promotions applied to this invoice
 *   - total_cents         -> subtotal minus discounts
 *   - amount_due_cents    -> what is actually collected today (total minus any customer balance)
 *
 * We still walk the lines to separate the fresh period from the proration credit, because those are
 * shown as distinct rows, but every total below comes straight from the response.
 *
 * The preview is requested with AI Tracking already in price_items (see buildPriceItems) and
 * with_free_ai_tracking set, so the AI Tracking line comes back from the API — at $0 while its
 * 30-day trial runs — rather than being assumed by this page. `hasAiTrackingLine` tells the summary
 * whether it needs to add its own $0 row as a fallback.
 *
 * Falls back to the current subscription items only when the preview is unavailable, so the page
 * still renders.
 */
export function buildBillingSummary(current, preview, aiPrice) {
    const currency = preview?.currency || current?.items?.[0]?.currency || 'usd';

    if (preview) {
        const { lines, recurringCents, creditCents, prorationChargeCents } = splitPreviewLines(
            preview,
            aiPrice,
        );

        const subtotalCents =
            preview.subtotal_cents != null
                ? Number(preview.subtotal_cents)
                : recurringCents + prorationChargeCents - creditCents;
        const discountCents = Math.abs(Number(preview.total_discount_cents) || 0);
        const totalCents =
            preview.total_cents != null
                ? Number(preview.total_cents)
                : subtotalCents - discountCents;
        const amountDueCents =
            preview.amount_due_cents != null ? Number(preview.amount_due_cents) : totalCents;
        // Anything Stripe applied between total and amount due (customer credit balance).
        const balanceAppliedCents = Math.max(0, totalCents - amountDueCents);

        const aiLines = lines.filter((line) => line.isAiTracking);
        // The AI Tracking charge that sits inside `recurringCents` / `subtotalCents`. The trial is
        // free for its first 30 days, so the page presents AI Tracking at $0 today and nets this
        // amount out of the new plan price and the invoice totals (see the render layer).
        const aiRecurringCents = aiLines
            .filter((line) => !line.isProration)
            .reduce((total, line) => total + line.amountCents, 0);

        return {
            lines,
            recurringCents,
            aiTrackingCents: aiLines.reduce((total, line) => total + line.amountCents, 0),
            aiRecurringCents,
            hasAiTrackingLine: aiLines.length > 0,
            creditCents,
            prorationChargeCents,
            subtotalCents,
            discountCents,
            balanceAppliedCents,
            totalCents,
            amountDueCents,
            currency,
            nextPaymentDate: preview.next_payment_date || null,
            invalidatedCouponIds: preview.invalidated_coupon_ids || [],
            requiresDiscountAcknowledgement: Boolean(
                preview.requires_discount_loss_acknowledgement,
            ),
            fromPreview: true,
        };
    }

    const recurringCents = sumCurrentItemsCents(current?.items);
    return {
        lines: [],
        recurringCents,
        aiTrackingCents: 0,
        aiRecurringCents: 0,
        hasAiTrackingLine: false,
        creditCents: 0,
        prorationChargeCents: 0,
        subtotalCents: recurringCents,
        discountCents: 0,
        balanceAppliedCents: 0,
        totalCents: recurringCents,
        amountDueCents: recurringCents,
        currency,
        nextPaymentDate: null,
        invalidatedCouponIds: [],
        requiresDiscountAcknowledgement: false,
        fromPreview: false,
    };
}
